import { Badge } from "../common/Badge";
import { EmptyState } from "../common/EmptyState";
import { rollUpSites } from "./siteRollup";
import { groupByAccount } from "./accountGrouping";

/**
 * The second layer of the dashboard (#282, absorbing #266).
 *
 * A grid of sites, each compressed to one line. This is what makes the
 * dashboard scale: twenty site cards is a readable grid, where twenty
 * sites' worth of hives is a page nobody reads. The card summarises; the
 * hives live one click down.
 *
 * The colour scheme is #266's, unchanged: green for a yard where
 * everything is current, a lighter green when work is coming up, amber
 * when something is due, red when a hive there has an open issue.
 *
 * Colour is never the only signal. Each card states its condition in
 * words as well, because a red square means nothing to someone who cannot
 * distinguish it from the amber one.
 */

const STATE_STYLE = {
  issues: { tone: "danger", label: "Needs attention", dot: "var(--color-danger)" },
  overdue: { tone: "warning", label: "Work due", dot: "var(--color-warning)" },
  soon: { tone: "neutral", label: "Coming up", dot: "var(--color-success)" },
  current: { tone: "success", label: "Up to date", dot: "var(--color-success)" },
};

export function SiteGrid({ hives, openIssues, onOpenSite }) {
  // Split by account before rolling up (#286). A keeper who helps at two
  // other apiaries was shown every yard in one grid with nothing saying
  // whose was whose.
  //
  // Only when there IS more than one: a beekeeper on their own gets a
  // heading answering a question nobody asked, and that is most people.
  const groups = groupByAccount(hives);
  const grouped = groups.length > 1;

  if (!grouped) {
    const sites = rollUpSites(hives, openIssues);
    if (sites.length === 0) {
      return <EmptyState title="No hives yet" description="Add your first hive to get started." />;
    }
    return <SiteCards sites={sites} onOpenSite={onOpenSite} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      {groups.map((group) => (
        <section key={group.account_user_id ?? "mine"}>
          <h3 style={{ margin: "0 0 var(--space-2)", fontSize: "var(--font-size-md)" }}>
            {group.label}
            <span style={{ color: "var(--color-text-muted)", fontWeight: 400 }}>
              {" "}— {group.hives.length} {group.hives.length === 1 ? "hive" : "hives"}
            </span>
          </h3>
          <SiteCards
            sites={rollUpSites(group.hives, openIssues)}
            onOpenSite={onOpenSite}
          />
        </section>
      ))}
    </div>
  );
}

/** The grid itself, so grouped and ungrouped render identically. */
function SiteCards({ sites, onOpenSite }) {
  if (sites.length === 0) {
    return <EmptyState title="No hives yet" description="Add your first hive to get started." />;
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
        gap: "var(--space-3)",
      }}
    >
      {sites.map((site) => {
        const style = STATE_STYLE[site.state];
        return (
          <button
            key={site.site_id ?? site.site_name}
            type="button"
            onClick={() => onOpenSite?.(site)}
            style={{
              textAlign: "left",
              font: "inherit",
              cursor: "pointer",
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              // The state reads on the edge, so a wall of cards can be
              // scanned without reading any of them.
              borderLeft: `4px solid ${style.dot}`,
              borderRadius: "var(--radius-md)",
              padding: "var(--space-3)",
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-1)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "var(--space-2)" }}>
              <strong>{site.site_name}</strong>
              <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                {site.hive_count} {site.hive_count === 1 ? "hive" : "hives"}
              </span>
            </div>

            <div>
              <Badge tone={style.tone}>{style.label}</Badge>
            </div>

            <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
              {site.headline}
            </span>

            {/* Where to start once you get there. Only worth saying when
                something is actually wrong -- on a healthy yard it would
                be pointing at nothing. */}
            {site.state !== "current" && site.worst_hive && (
              <span style={{ fontSize: "var(--font-size-sm)" }}>
                Start with hive {site.worst_hive.hive_number}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
